import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, KeyRound } from "lucide-react";
import FadeIn, { SectionHeading } from "./FadeIn";

// ---------------------------------------------------------------------------
// RECENTLY FUNDED — real milestones only, published with the client's
// permission. Each entry is one of Akashdeep's own funded-deal graphics.
// ---------------------------------------------------------------------------
const FUNDED = [
  {
    img: "/funded/funded-816k.webp",
    title: "$816,000 Funded",
    detail: "First-time home purchase",
    poster: true,
  },
  {
    img: "/funded/funded-572k.webp",
    title: "$572,000 Funded",
    detail: "First-time home purchase",
    poster: true,
  },
  {
    img: "/funded/funded-444k.webp",
    title: "$444,000 Funded",
    detail: "First home · Lethbridge, AB",
    poster: true,
  },
  {
    img: "/funded/funded-175k.webp",
    title: "$175,000 Funded",
    detail: "Refinance · Debt consolidation",
    poster: true,
  },
  {
    img: "/funded/funded-112k.webp",
    title: "$112,500 Funded",
    detail: "Refinance",
    poster: true,
  },
];

const AUTOPLAY_MS = 3000;

function FundedCarousel({ items }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + items.length) % items.length),
    [items.length]
  );

  useEffect(() => {
    if (paused || items.length < 2) return;
    timer.current = setInterval(() => go(1), AUTOPLAY_MS);
    return () => clearInterval(timer.current);
  }, [paused, items.length, go]);

  return (
    <div
      data-testid="funded-carousel"
      className="relative rounded-md overflow-hidden border border-gold/25 h-[420px] lg:h-[520px] group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.12}
          onDragEnd={(_, info) => {
            if (info.offset.x < -60) go(1);
            else if (info.offset.x > 60) go(-1);
          }}
        >
          {items[index].poster ? (
            <>
              {/* blurred cinematic letterbox behind the full poster */}
              <img
                src={items[index].img}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40 scale-110"
              />
              <motion.img
                src={items[index].img}
                alt={items[index].title}
                className="relative w-full h-full object-contain"
                initial={{ scale: 1.04 }}
                animate={{ scale: 1 }}
                transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
              />
            </>
          ) : (
            <motion.img
              src={items[index].img}
              alt={items[index].title}
              className="w-full h-full object-cover"
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
            />
          )}
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent pointer-events-none" />

      <div className="absolute bottom-0 inset-x-0 p-8 lg:p-10 flex items-end justify-between gap-6">
        {/* caption sits in the blurred margin on desktop; hidden on mobile where
            the poster itself carries the information */}
        <div className="hidden sm:block">
          <p className="gold-label mb-2.5">Recently Funded</p>
          <h3 className="font-serif text-2xl lg:text-3xl text-ivory">{items[index].title}</h3>
          {items[index].detail && (
            <p className="mt-2 text-sm text-ivory/55 font-light">{items[index].detail}</p>
          )}
        </div>
        <div className="flex gap-2.5 shrink-0">
          <button
            data-testid="funded-prev-btn"
            onClick={() => go(-1)}
            aria-label="Previous funded property"
            className="w-11 h-11 rounded-sm border border-white/25 bg-black/40 backdrop-blur-md text-ivory/80 hover:text-gold hover:border-gold/60 transition-colors duration-300 flex items-center justify-center"
          >
            <ArrowLeft size={15} strokeWidth={1.5} />
          </button>
          <button
            data-testid="funded-next-btn"
            onClick={() => go(1)}
            aria-label="Next funded property"
            className="w-11 h-11 rounded-sm border border-white/25 bg-black/40 backdrop-blur-md text-ivory/80 hover:text-gold hover:border-gold/60 transition-colors duration-300 flex items-center justify-center"
          >
            <ArrowRight size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="absolute top-6 right-6 flex gap-1.5" data-testid="funded-pagination">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-[3px] rounded-full transition-all duration-500 ${
              i === index ? "w-8 bg-gold" : "w-3 bg-white/30 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function FundedEmptyState() {
  return (
    <FadeIn>
      <div
        data-testid="funded-empty-state"
        className="relative rounded-md overflow-hidden border border-gold/25 h-[380px] lg:h-[460px] flex items-center justify-center text-center"
      >
        <img
          src="/homes/hero-interior.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-charcoal/70" />
        <div className="relative px-8">
          <div className="mx-auto w-14 h-14 rounded-sm border border-gold/40 bg-gold/10 flex items-center justify-center mb-7">
            <KeyRound size={20} strokeWidth={1.5} className="text-gold" />
          </div>
          <p className="font-serif text-2xl lg:text-3xl text-ivory leading-snug">
            Real properties. Real milestones. New keys.
          </p>
          <p className="mt-4 text-sm text-ivory/50 font-light tracking-wide">
            Property highlights coming soon.
          </p>
        </div>
      </div>
    </FadeIn>
  );
}

export default function RecentlyFunded() {
  return (
    <section id="recently-funded" data-testid="recently-funded-section" className="relative py-24 lg:py-32 bg-charcoal/95">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          label="Recently Funded"
          align="center"
          title={
            <>
              MILESTONES, <span className="gold-text-gradient italic">NOT PROMISES.</span>
            </>
          }
          sub="A running record of funded homes and the people holding new keys — published with permission, never staged."
        />
        <div className="mt-14">
          {FUNDED.length > 0 ? <FundedCarousel items={FUNDED} /> : <FundedEmptyState />}
        </div>
      </div>
    </section>
  );
}
